import {
  Children,
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {Tabs} from "@interstate/components/Tabs";
import {DEFAULT_NAME, ManagedTabsState} from "./ManagedTabsState";
import {ManagedTabProps} from "./ManagedTabProps";
import {ManagedTabEvent, ManagedTabListener} from "./ManagedTabEvent";
import {isManagedTabElement} from "./isManagedTabElement";
import {Placeholder} from "./Placeholder";

export type ManagedTabElements =
  | ReactElement<ManagedTabProps>[]
  | ReactElement<ManagedTabProps>
  | any;

export interface ManagedTabsProps {
  /**
   * The value assigned to the id and data-testid props of the {@link Tabs}
   * component
   */
  id: string;

  /**
   * The name assigned to this group of tabs; required when more than
   * one instance of this component is present on a page
   */
  name?: string;

  /**
   * The initial tab to display
   */
  initial: string;

  /**
   * Callback function invoked when a tab is activated
   */
  onTabActivated?: ManagedTabListener;

  /**
   * A collection of {@link ManagedTab} elements
   */
  children: ManagedTabElements;
}

/**
 * Decodes the {@link ManagedTabsState} from the {@link Location#hash}
 *
 * @param hash The value of {@link Location#hash}
 */
const fromHash = (hash?: string | null): ManagedTabsState => {
  return ManagedTabsState.decode(hash?.startsWith("#") ? hash.slice(1) : hash);
};

/**
 * Encodes the {@link ManagedTabsState} for use as the {@link Location#hash}
 *
 * @return An encoded version of this {@link ManagedTabsState} for use as {@link Location#hash}
 */
const toHash = (state: ManagedTabsState): string => {
  return `#${state.encode()}`;
};

/**
 * The {@link ManagedTabs} component uses a codec to store active tab state in the
 * URL to enable deep-linking.
 *
 * @constructor
 */
export const ManagedTabs: FC<ManagedTabsProps> = ({
  id,
  name = DEFAULT_NAME,
  initial,
  onTabActivated = (_event: ManagedTabEvent) => {},
  children
}) => {
  const location = useLocation();
  const history = useHistory();
  const [tabsState, setTabsState] = useState<ManagedTabsState>(
    () => new ManagedTabsState()
  );

  const tabs = useMemo<ReactElement<ManagedTabProps>[]>(
    () => Children.toArray(children).filter(isManagedTabElement),
    [children]
  );

  // For deep-linking
  const onActivate = useCallback(
    (index: number): void => {
      const tabId = tabs[index].props.tabId;
      const label = tabs[index].props.label;
      const state = tabsState.setActive(name, tabId);
      history.replace({...location, hash: toHash(state)});
      onTabActivated({
        name,
        tabId,
        label
      });
      setTabsState(state);
    },
    [history, location, name, onTabActivated, tabs, tabsState]
  );

  useEffect(() => setTabsState(fromHash(location.hash)), [location.hash]);

  return (
    <Tabs
      id={id}
      data-testid={id}
      onActivate={onActivate}
      tabs={tabs.map((tab, index) => {
        const active = tab.props.tabId === tabsState.getActive(name, initial);
        // prettier-ignore
        const component = !active && tab.props.unmountInactive
            ? <Placeholder />
            : tab;
        return {
          "key": tab.props.tabId,
          "data-testid": `${id}-${index}`,
          "label": tab.props.label,
          active,
          component
        };
      })}
    />
  );
};
